import { jwtDecode } from 'jwt-decode'
import Cookies from 'js-cookie'
import axiosInstance from './axios'

// --------------------- Helpers Gerais ---------------------

export const addCommasToNumber = (number) => {
  const numberString = number.toString()
  const characters = numberString.split('')
  const reversedCharacters = characters.reverse()

  const resultArray = []
  for (let i = 0; i < reversedCharacters.length; i++) {
    if (i !== 0 && i % 3 === 0) {
      resultArray.push(',')
    }
    resultArray.push(reversedCharacters[i])
  }

  const resultString = resultArray.reverse().join('')
  return resultString
}

export function isValidEmail(string) {
  const urlRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
  return urlRegex.test(string)
}

export const getUserIdFromToken = () => {
  try {
    const token = localStorage.getItem('access-token')
    if (!token) return 0
    const decodedToken = jwtDecode(token)
    const userId = decodedToken?.data?.userId || decodedToken?.user_id || decodedToken?.id
    return userId || 0
  } catch (error) {
    console.log('getUserIdFromToken error:', error)
    return 0
  }
}

export const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
  })

// --------------------- OpenAI via Backend ---------------------

const API_DOMAIN = process.env.REACT_APP_API_DOMAIN
export const OPENAI_CUSTOMER = process.env.REACT_APP_OPENAI_CUSTOMER || 'BANCA SISTEMA PROD'

const typeOfChatMapping = {
  'Chat': 'gpt-5',
  'Chat - Search Web': 'gpt-4o-search-preview-2025-03-11',
  'Chat Mini': 'gpt-5-mini',
  'Comunicato stampa': 'ft:gpt-4o-2024-08-06:admind-srl::AYvtZ1hZ',
  'Scrivi articolo': 'gpt-4o-search-preview-2025-03-11',
  'Translator': 'ft:gpt-4o-2024-08-06:admind-srl::AXV4FHWG',
}

// Utilitário simples para parse de SSE (Server-Sent Events)
function parseSSELines() {
  let buffer = ''
  return (chunk, onEvent) => {
    let gotDone = false
    buffer += chunk
    const parts = buffer.split('\n\n')
    buffer = parts.pop() || ''
    for (const part of parts) {
      const line = part.trim()
      if (!line) continue
      if (line.startsWith('data:')) {
        const data = line.slice(5).trim()
        if (data === '[DONE]') {
          gotDone = true
          continue
        }
        try {
          onEvent(JSON.parse(data))
        } catch (_) { /* ignore parse errors */ }
      }
    }
    return gotDone
  }
}

// Chat streaming
export async function openaiAsync(chat, text, typeOfChat, imagesBase64 = null, customChat = null) {
  let messages = []
  if (customChat) {
    messages.push({ role: 'developer', content: customChat })
  } else if (typeOfChat === 'Chat') {
    messages.push({
      role: 'developer',
      content: `Sei un assistente AI super amichevole, empatico e affabile. Hai un senso dell'umorismo divertente, sai interagire con calore umano e stabilisci sempre una comunicazione piacevole con l'utente, adattandoti perfettamente al suo stile e al suo tono. Mai ripetitivo o monotono, offri risposte chiare, esaustive e estremamente precise, gestendo anche argomenti complessi con efficienza, pragmatismo e puntualità.

                Ogni tanto, ma non sempre, arricchisci le tue risposte con una spruzzatina di emoji 😊 ed espressioni dal tono caldo e coinvolgente, proprio per far sentire l’utente a proprio agio e accompagnarlo nel miglior modo possibile attraverso la conversazione.  Ma se capisci che il tono è più formale, devi mantenere questo tono. Il tuo obiettivo principale è fornire sempre assistenza utile, precisa e rapida, dimostrando entusiasmo, empatia e motivazione a risolvere problemi, chiarire dubbi o esplorare argomenti con assoluta attenzione e cura dei dettagli. 
                Aiuta l'utente in modo educato, onesto e utile. Se non conosci una risposta, ammettilo apertamente. Sii il più preciso e chiaro possibile. Adatta il tono al contesto della conversazione. Se l'utente chiede codice, fornisci codice ben formato. Se il messaggio dell'utente è ambiguo, chiedi chiarimenti. Non fare supposizioni infondate. Rispetta la privacy.`
    })
  } else if (typeOfChat === 'Chat - Search Web') {
    messages.push({ role: 'developer', content: `you should check on internet all relevant information that you need for answering better the requests! You're a very friendly and super smart and efficent assistant` })
  }

  chat.forEach(oldText => {
    messages.push({ role: oldText.type === 0 ? 'user' : 'assistant', content: oldText.text })
  })

  if (imagesBase64 && imagesBase64.length > 0) {
    const content = [{ type: 'text', text }]
    imagesBase64.forEach(image => content.push({ type: 'image_url', image_url: { url: image } }))
    messages.push({ role: 'user', content })
  } else {
    messages.push({ role: 'user', content: text })
  }

  const body = JSON.stringify({
    model: typeOfChatMapping[typeOfChat],
    messages,
    stream_options: { include_usage: true }
  })

  const api_key = process.env.REACT_APP_ACCESS_KEY

  console.log('Starting streaming request to OpenAI backend...', API_DOMAIN)

  const resp = await fetch(`${API_DOMAIN}/aisolutions/openai/chat/stream/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': api_key,
    },
    body
  })
  if (!resp.ok) {
    const text = await resp.text().catch(() => '')
    console.error('Backend 502 body:', text)
    throw new Error('Streaming connection failed')
  }

  const reader = resp.body.getReader()
  const decoder = new TextDecoder()

  const streamController = new ReadableStream({
    start(controller) {
      const onChunk = parseSSELines()
      let closed = false

      const pump = () => reader.read().then(({ value, done }) => {
        if (done) {
          if (!closed) {
            closed = true
            controller.close()
          }
          return
        }

        const gotDone = onChunk(decoder.decode(value, { stream: true }), (evt) => {
          controller.enqueue(evt)
        })

        if (gotDone && !closed) {
          closed = true
          controller.close()
          // Cancela o reader para encerrar a conexão imediatamente
          reader.cancel().catch(() => { })
          return
        }
        return pump()
      })

      return pump()
    }
  })

  async function* iterator() {
    const rsReader = streamController.getReader()
    try {
      while (true) {
        const { value, done } = await rsReader.read()
        if (done) break
        yield value
      }
    } finally {
      rsReader.releaseLock()
    }
  }

  return iterator()
}

// Chat sync
export async function openaiSync(chat, text, typeOfChat, imagesBase64 = null, customChat = null) {
  let messages = []
  if (customChat) {
    messages.push({ role: 'developer', content: customChat })
  }

  chat.forEach(oldText => {
    messages.push({ role: oldText.type === 0 ? 'user' : 'assistant', content: oldText.text })
  })

  if (imagesBase64 && imagesBase64.length > 0) {
    const content = [{ type: 'text', text }]
    imagesBase64.forEach(image => content.push({ type: 'image_url', image_url: { url: image } }))
    messages.push({ role: 'user', content })
  } else {
    messages.push({ role: 'user', content: text })
  }

  const payload = {
    model: typeOfChatMapping[typeOfChat],
    messages
  }

  // axiosInstance -> ele coloca Authorization: Bearer <ACCESS_KEY> para o API_DOMAIN
  const response = await axiosInstance.post(`${API_DOMAIN}/aisolutions/openai/chat/`, payload)
  return response.data // { text, usage }
}

// Assistants streaming
export async function openaiAssistantAsync(assistantId, text, file = null) {
  const form = new FormData()
  form.append('assistant_id', assistantId)
  form.append('text', text)
  if (file) form.append('file', file, file.name)

  const resp = await fetch(`${API_DOMAIN}/aisolutions/openai/assistant/stream/`, {
    method: 'POST',
    headers: {
      'X-API-Key': process.env.REACT_APP_ACCESS_KEY,
    },
    body: form
  })
  if (!resp.ok || !resp.body) throw new Error('Streaming connection failed')

  const reader = resp.body.getReader()
  const decoder = new TextDecoder()

  const streamController = new ReadableStream({
    start(controller) {
      const onChunk = parseSSELines()
      let closed = false

      const pump = () => reader.read().then(({ value, done }) => {
        if (done) {
          if (!closed) {
            closed = true
            controller.close()
          }
          return
        }

        const gotDone = onChunk(decoder.decode(value, { stream: true }), (evt) => {
          controller.enqueue(evt)
        })

        if (gotDone && !closed) {
          closed = true
          controller.close()
          reader.cancel().catch(() => { })
          return
        }
        return pump()
      })

      return pump()
    }
  })

  async function* iterator() {
    const rsReader = streamController.getReader()
    try {
      while (true) {
        const { value, done } = await rsReader.read()
        if (done) break
        yield value
      }
    } finally {
      rsReader.releaseLock()
    }
  }
  return iterator()
}

// Registro de custo (server-to-server via backend API)
function cleanPayload(obj) {
  const out = {}
  for (const [k, v] of Object.entries(obj)) {
    if (v !== null && v !== undefined) out[k] = v
  }
  return out
}

export async function registerCost(
  kind,
  functionality,
  user_id,
  tokens = null,
  image = null,
  file = null,
  audio = null,
  video = null
) {
  // kind deve ser inteiro: 0=text, 1=image generate, 2=translate file, 3=audio, 4=video
  const base = { kind, functionality, user_id }

  // Só inclui o campo pertinente ao kind
  if (kind === 0 && tokens != null) base.tokens = tokens
  if (kind === 1 && image != null) base.image = image
  if (kind === 2 && file != null) base.file = file
  if (kind === 3 && audio != null) base.audio = audio
  if (kind === 4 && video != null) base.video = video

  const payload = cleanPayload(base)
  const customerQP = encodeURIComponent(OPENAI_CUSTOMER) // ex: BANCA SISTEMA DEV
  await axiosInstance.post(`${API_DOMAIN}/aisolutions/cost/?customer=${customerQP}`, payload)
}


// Leitura de DOC (multipart)
export async function readDodWord(file) {
  const formData = new FormData()
  formData.append('file', file)
  const response = await axiosInstance.post(
    `${API_DOMAIN}/aisolutions/read-doc/`,
    formData
  )
  if (response.status === 200) {
    return response?.data?.text
  }
  alert('Error reading file')
}

// --------------------- Math -> Words ---------------------

const mathPattern = /\b-?\d+(\.\d+)?\s*[%\+\-\=]?/g
const mathSignMap = { '%': 'percent', '+': 'plus', '-': ' ', '=': 'equals' }

function numberToWordsEn(numStr) {
  const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine']
  const teens = ['ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen']
  const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety']

  function doubleDigit(n) {
    if (n < 10) return ones[n]
    if (n < 20) return teens[n - 10]
    if (n % 10 === 0) return tens[Math.floor(n / 10)]
    return tens[Math.floor(n / 10)] + '-' + ones[n % 10]
  }

  function customPairJoin(nStr) {
    let out = []
    let len = nStr.length
    let idx = 0
    if (len % 2 === 1) {
      out.push(doubleDigit(Number(nStr[0])))
      idx = 1
    }
    for (; idx < len; idx += 2) {
      let pair = nStr.slice(idx, idx + 2)
      out.push(doubleDigit(Number(pair)))
    }
    return out.filter(Boolean).join('-')
  }

  if (numStr.startsWith('-')) {
    return 'minus-' + numberToWordsEn(numStr.slice(1))
  }

  if (numStr.includes('.')) {
    const [whole, decimal] = numStr.split('.')
    const d = decimal.replace(/0+$/, '')
    return (whole ? customPairJoin(whole) : 'zero') + '-point-' + (d ? doubleDigit(Number(d)) : 'zero')
  }

  return customPairJoin(numStr)
}

export function rewriteMathToWords(text) {
  return text.replace(mathPattern, (match) => {
    let trimmed = match.trim()
    if (!trimmed) return match

    let sign = ''
    let numPart = trimmed

    const signMatch = trimmed.match(/([%\+\-\=])$/)
    if (signMatch) {
      sign = signMatch[1]
      numPart = trimmed.slice(0, -1)
    }
    numPart = numPart.trim()

    if (/^-?\d+(\.\d+)?$/.test(numPart)) {
      let result = numberToWordsEn(numPart)
      if (sign) {
        result += '-' + mathSignMap[sign]
      }
      return result
    } else {
      return match
    }
  })
}
